import * as THREE from 'three';
import { Entity, type EntityCtx } from '../Entity';
import { Explosion } from './Explosion';

/**
 * Crash debris system. When a vehicle is destroyed it bursts into several solid chunks
 * that fly out, tumble, bounce and skitter across the terrain, trailing fire/smoke, and
 * then each chunk cooks off in its own little explosion. Each vehicle category has a pool
 * of distinct piece shapes ("several variants of each") and every spawn randomises which
 * pieces appear, their size, spin, scatter and fuse — so no two crashes look the same.
 */

const GRAVITY = 42;            // u/s² — snappy fall
const noShadow = (o: THREE.Object3D) => { (o as THREE.Mesh).castShadow = false; (o as THREE.Mesh).receiveShadow = false; };

export interface WreckPalette { body: number; trim: number; metal: number; dark: number; wood: number; }

/** Preset liveries so each vehicle's debris reads as its own (red Fokker, blue ally, …). */
export const WRECK_PALETTES: Record<string, WreckPalette> = {
  fokker:      { body: 0xa02424, trim: 0xb43030, metal: 0x3a3a38, dark: 0x161614, wood: 0x3a2a1a },
  alliedPlane: { body: 0x4a6a8a, trim: 0x6a86a0, metal: 0x3a3a38, dark: 0x161614, wood: 0x3a2a1a },
  enemyPlane:  { body: 0x6f6a4e, trim: 0x84805f, metal: 0x3a3a38, dark: 0x161614, wood: 0x3a2a1a },
  enemyTank:   { body: 0x3e4a36, trim: 0x2a3324, metal: 0x2a2a2c, dark: 0x141414, wood: 0x3a2a1a },
  alliedTank:  { body: 0x6b6242, trim: 0x504830, metal: 0x2a2a2c, dark: 0x141414, wood: 0x3a2a1a },
  gun:         { body: 0x4b5320, trim: 0x9c8a5a, metal: 0x2c2c2e, dark: 0x141414, wood: 0x3a2a1a },
};

export type WreckKind = 'plane' | 'tank' | 'gun';

const rand = (a: number, b: number) => a + Math.random() * (b - a);
const mat = (color: number) => new THREE.MeshStandardMaterial({ color, roughness: 0.9, flatShading: true });
const metalMat = (color: number) => new THREE.MeshStandardMaterial({ color, roughness: 0.45, metalness: 0.6, flatShading: true });

// --- Piece factories: each returns one debris chunk mesh. Several distinct shapes per
//     category give visual variety; internal randomisation varies them further. ---
type PieceFactory = (p: WreckPalette) => THREE.Object3D;

const PLANE_PIECES: PieceFactory[] = [
  // Wing shard — flat panel with a darker trailing strip.
  (p) => {
    const g = new THREE.Group();
    const span = rand(2.2, 4.8);
    const panel = new THREE.Mesh(new THREE.BoxGeometry(span, 0.16, rand(0.9, 1.5)), mat(p.trim));
    g.add(panel);
    const edge = new THREE.Mesh(new THREE.BoxGeometry(span * 0.9, 0.18, 0.22), mat(p.dark));
    edge.position.z = 0.5;
    g.add(edge);
    return g;
  },
  // Fuselage chunk — tapered body block.
  (p) => {
    const m = new THREE.Mesh(new THREE.BoxGeometry(rand(0.8, 1.3), rand(0.6, 1.0), rand(1.4, 2.8)), mat(p.body));
    return m;
  },
  // Engine cowl ring — short metal cylinder.
  (p) => new THREE.Mesh(new THREE.CylinderGeometry(rand(0.45, 0.65), rand(0.5, 0.7), rand(0.5, 0.8), 12, 1), metalMat(p.metal)),
  // Wheel — rubber disc on a hub.
  (p) => {
    const g = new THREE.Group();
    const r = rand(0.34, 0.5);
    const tyre = new THREE.Mesh(new THREE.CylinderGeometry(r, r, 0.16, 16), mat(p.dark));
    tyre.rotation.z = Math.PI / 2;
    g.add(tyre);
    return g;
  },
  // Broken propeller blade — long thin tapered slat.
  (p) => new THREE.Mesh(new THREE.BoxGeometry(0.18, rand(1.6, 2.6), 0.42, 1, 3, 1), mat(p.wood)),
  // Strut / spar splinter.
  (p) => new THREE.Mesh(new THREE.CylinderGeometry(0.07, 0.09, rand(1.0, 2.0), 8), mat(p.wood)),
];

const TANK_PIECES: PieceFactory[] = [
  // Armour plate — heavy flat slab.
  (p) => new THREE.Mesh(new THREE.BoxGeometry(rand(1.3, 2.4), rand(0.25, 0.5), rand(1.2, 2.2)), mat(p.body)),
  // Track section — dark segmented run.
  (p) => {
    const g = new THREE.Group();
    const len = rand(1.6, 3.0);
    const belt = new THREE.Mesh(new THREE.BoxGeometry(0.7, 0.5, len), mat(p.dark));
    g.add(belt);
    const lugs = Math.max(2, Math.floor(len / 0.5));
    for (let i = 0; i < lugs; i++) {
      const lug = new THREE.Mesh(new THREE.BoxGeometry(0.85, 0.14, 0.16), mat(0x0e0e0e));
      lug.position.z = -len / 2 + 0.25 + i * (len / lugs);
      g.add(lug);
    }
    return g;
  },
  // Road wheel.
  (p) => {
    const w = new THREE.Mesh(new THREE.CylinderGeometry(rand(0.45, 0.7), rand(0.45, 0.7), 0.4, 16), metalMat(p.metal));
    w.rotation.z = Math.PI / 2;
    return w;
  },
  // Turret / cupola chunk.
  (p) => new THREE.Mesh(new THREE.CylinderGeometry(rand(0.6, 0.95), rand(0.7, 1.05), rand(0.6, 0.9), 12, 1), mat(p.body)),
  // Hatch plate.
  (p) => new THREE.Mesh(new THREE.BoxGeometry(rand(0.6, 0.9), 0.16, rand(0.6, 0.9)), mat(p.trim)),
  // Gun-barrel fragment.
  (p) => new THREE.Mesh(new THREE.CylinderGeometry(0.15, 0.18, rand(1.2, 2.2), 12), metalMat(p.metal)),
];

const GUN_PIECES: PieceFactory[] = [
  // Barrel section.
  (p) => new THREE.Mesh(new THREE.CylinderGeometry(0.08, 0.11, rand(1.0, 1.8), 12), metalMat(p.metal)),
  // Sandbag.
  (p) => {
    const m = new THREE.Mesh(new THREE.BoxGeometry(rand(0.8, 1.1), rand(0.45, 0.65), rand(0.6, 0.85), 2, 2, 2), mat(p.trim));
    m.scale.y = 0.85;
    return m;
  },
  // Curved shield fragment.
  (p) => new THREE.Mesh(new THREE.BoxGeometry(rand(0.7, 1.2), rand(0.5, 0.9), 0.08), metalMat(p.metal)),
  // Pintle / mount lump.
  (p) => new THREE.Mesh(new THREE.CylinderGeometry(rand(0.25, 0.4), rand(0.3, 0.5), rand(0.4, 0.7), 12), metalMat(p.metal)),
  // Ammo crate.
  (p) => new THREE.Mesh(new THREE.BoxGeometry(rand(0.5, 0.75), rand(0.3, 0.45), rand(0.4, 0.6)), mat(p.body)),
];

const POOLS: Record<WreckKind, PieceFactory[]> = { plane: PLANE_PIECES, tank: TANK_PIECES, gun: GUN_PIECES };

class DebrisChunk extends Entity {
  private vel: THREE.Vector3;
  private angVel: THREE.Vector3;
  private fuse: number;
  private rest: number;
  private burns: boolean;
  private smokeT = 0;

  private impact?: { radius: number; damage: number };
  private landed = false;

  constructor(pos: THREE.Vector3, vel: THREE.Vector3, angVel: THREE.Vector3, mesh: THREE.Object3D, fuse: number, rest: number, burns: boolean, impact?: { radius: number; damage: number }) {
    super('fx'); // 'fx' → ignored by the collision pass; it manages its own ground check
    this.group.position.copy(pos);
    this.group.rotation.set(Math.random() * 6.28, Math.random() * 6.28, Math.random() * 6.28);
    mesh.traverse(noShadow);
    this.group.add(mesh);
    this.vel = vel; this.angVel = angVel; this.fuse = fuse; this.rest = rest; this.burns = burns;
    this.impact = impact;
    this.radius = 0.5;
  }

  update(ctx: EntityCtx): void {
    const dt = ctx.dt;
    this.vel.y -= GRAVITY * dt;
    this.group.position.addScaledVector(this.vel, dt);
    this.group.rotation.x += this.angVel.x * dt;
    this.group.rotation.y += this.angVel.y * dt;
    this.group.rotation.z += this.angVel.z * dt;

    // Terrain contact — bounce while fast, then skitter to a crawl with friction.
    const gy = ctx.game.gameScene.heightAt(this.group.position.x, this.group.position.z) + this.rest;
    if (this.group.position.y <= gy) {
      this.group.position.y = gy;
      // First ground contact of the designated impactor chunk craters the crash site once.
      if (!this.landed) {
        this.landed = true;
        if (this.impact) ctx.game.crashImpact(ctx, this.group.position.clone(), this.impact.radius, this.impact.damage);
      }
      if (this.vel.y < -2.5) {
        this.vel.y = -this.vel.y * 0.42;           // bounce
        this.vel.x *= 0.7; this.vel.z *= 0.7;
        this.angVel.multiplyScalar(0.7);
      } else {
        this.vel.y = 0;
        const f = Math.max(0, 1 - 3.5 * dt);        // ground friction
        this.vel.x *= f; this.vel.z *= f;
        const af = Math.max(0, 1 - 2.5 * dt);
        this.angVel.set(this.angVel.x * af, this.angVel.y * af, this.angVel.z * af);
      }
    }

    // Burning trail.
    if (this.burns) {
      this.smokeT += dt;
      if (this.smokeT >= 0.1) {
        this.smokeT = 0;
        new Explosion(this.group.position.clone(), 5, 0.6, 'smoke').setDrift(new THREE.Vector3(0, 5, 0)).addTo(ctx);
        if (Math.random() < 0.4) new Explosion(this.group.position.clone(), 3, 0.25, 'fire').addTo(ctx);
      }
    }

    this.fuse -= dt;
    if (this.fuse <= 0) {
      const p = this.group.position.clone();
      new Explosion(p, 22, 0.55, 'fire').addTo(ctx);
      new Explosion(p.clone(), 14, 1.8, 'smoke').setDrift(new THREE.Vector3(0, 7, 0)).addTo(ctx);
      if (Math.random() < 0.4) ctx.game.audio.playBoom(0.3);
      this.dead = true;
    }
  }
}

export interface WreckOpts {
  /** Number of tumbling chunks. Default 7. */
  count?: number;
  /** Strength of the opening fireball + crash boom. Default 1.0; 0 disables. */
  boom?: number;
  /** Velocity the vehicle carried, partly inherited by the debris (for forward scatter). */
  baseVel?: THREE.Vector3;
  /** If set, the first chunk to hit the ground craters the crash site (trees/buildings/units). */
  impact?: { radius: number; damage: number };
}

/** Burst a destroyed vehicle into tumbling, self-detonating debris. */
export function spawnWreckage(ctx: EntityCtx, pos: THREE.Vector3, kind: WreckKind, palette: WreckPalette, opts: WreckOpts = {}) {
  const pool = POOLS[kind];
  const count = opts.count ?? 7;
  for (let i = 0; i < count; i++) {
    const make = pool[Math.floor(Math.random() * pool.length)];
    const mesh = make(palette);
    mesh.scale.multiplyScalar(1.7); // chunkier so debris reads clearly at gameplay range
    const a = Math.random() * Math.PI * 2;
    const horiz = rand(8, 28);
    const vel = new THREE.Vector3(Math.cos(a) * horiz, rand(10, 22), Math.sin(a) * horiz);
    if (opts.baseVel) vel.addScaledVector(opts.baseVel, 0.3);
    const angVel = new THREE.Vector3(rand(-11, 11), rand(-11, 11), rand(-11, 11));
    // Only the first chunk carries the crash-impact, so a downed plane craters its landing once.
    const chunk = new DebrisChunk(pos.clone(), vel, angVel, mesh, rand(1.0, 3.0), rand(0.5, 1.0), Math.random() < 0.7, i === 0 ? opts.impact : undefined);
    ctx.game.add(chunk);
  }
  const boom = opts.boom ?? 1.0;
  if (boom > 0) {
    new Explosion(pos.clone(), 44 * boom, 0.7, 'fire').addTo(ctx);
    new Explosion(pos.clone(), 28 * boom, 2.4, 'smoke').setDrift(new THREE.Vector3(0, 9, 0)).addTo(ctx);
    ctx.game.audio.playBoom(0.9 * boom);
  }
}
